import {
    Combobox,
    Transition,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
} from '@headlessui/react';
import { MiniAudioPlayer } from 'flow/components/AudioPlayers';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import { ReferralSound } from 'flow/types/Profile';

import { TypedMap } from 'flow/types/TypedMap';
import { homeActionUrlPath } from 'flow/utils';
import { twMerge } from 'tailwind-merge';
import { List } from 'immutable';
import React, { Fragment, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';

export interface SoundOption {
    id?: string;
    name: string;
    playbackUrl?: string;
}

interface Props {
    /**
     * Names the field for assistive tech. The visible label sits outside this
     * component, so only the caller can say what this combobox is for.
     */
    ariaLabel?: string;
    sound: SoundOption;
    onChange: (newValue: SoundOption) => void;
    className?: string;
}

const ReferralSoundCombobox = ({
    sound,
    onChange,
    className,
    ariaLabel,
}: Props) => {
    const { i18next } = useAppContext();

    const referralSounds =
        useAppSelector(
            (state) =>
                state.profiles.get('referal_sounds', List()) as TypedMap<
                    ReferralSound[]
                >,
        )
            .map((sound) => ({
                id: sound.get('key'),
                name: sound.get('description'),
                playbackUrl: sound.has('sound')
                    ? homeActionUrlPath(
                          '/' + sound.getIn(['sound', 'playbackURL']),
                      )
                    : null,
            }))
            .toArray() || [];

    referralSounds.unshift({
        id: null,
        name: i18next.t('settings.incoming_calls.profile.message.none'),
        playbackUrl: null,
    });

    const [query, setQuery] = useState('');

    const filteredSounds =
        query === ''
            ? referralSounds
            : referralSounds.filter((sound: SoundOption) =>
                  sound.name
                      .toLowerCase()
                      .replace(/\s+/g, '')
                      .includes(query.toLowerCase().replace(/\s+/g, '')),
              );

    return (
        <div className={twMerge('flex w-full gap-2', className)}>
            <Combobox value={sound} by="id" onChange={onChange}>
                <div className="relative w-full text-sm/4">
                    <ComboboxInput
                        aria-label={ariaLabel}
                        className="focus-ring w-full rounded-sm border-1 bg-gray-200 py-3 ps-3 pe-9 text-gray-900"
                        displayValue={(option: SoundOption) => option.name}
                        onChange={(event) => setQuery(event.target.value)}
                    />

                    <ComboboxButton className="absolute inset-y-0 end-0 flex items-center pe-2">
                        <Icon
                            className="size-5 text-gray-600"
                            iconKey="arrow_down"
                            aria-hidden="true"
                        />
                    </ComboboxButton>

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setQuery('')}
                    >
                        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                            {filteredSounds.map((option: SoundOption) => (
                                <ComboboxOption
                                    key={option.id}
                                    className={({ focus }) =>
                                        twMerge(
                                            'relative cursor-default py-2 ps-3 pe-9 select-none',
                                            focus
                                                ? 'bg-gray-200'
                                                : 'text-gray-900',
                                        )
                                    }
                                    value={option}
                                >
                                    {({ selected }) => (
                                        <span
                                            className={twMerge(
                                                'truncate text-gray-500',
                                                selected && 'font-medium',
                                            )}
                                        >
                                            {option.name}
                                        </span>
                                    )}
                                </ComboboxOption>
                            ))}
                        </ComboboxOptions>
                    </Transition>
                </div>
            </Combobox>

            <div className="flex items-center rounded-sm bg-gray-200">
                {!sound?.id || !sound?.playbackUrl ? (
                    // Placeholder holding the player's place until a sound is
                    // chosen -- a picture, not a disabled control. bg-gray-300
                    // is what BaseButton painted through disabled:bg-gray-300,
                    // and the square has to stay visible against the gray-200
                    // bar around it.
                    <span className="flex size-10 items-center justify-center rounded-sm bg-gray-300">
                        <Icon iconKey="volumeOff" className="text-gray-600" />
                    </span>
                ) : (
                    <MiniAudioPlayer
                        url={sound.playbackUrl}
                        uniqueId={sound.id}
                    />
                )}
            </div>
        </div>
    );
};

export default ReferralSoundCombobox;
